<template>
    <div class="bg-white py-6 md:py-10 border-0 md:border border-border rounded-lgr px-4 md:px-6 flex flex-col gap-6 md:gap-10 fs-mask">
        <div class="flex justify-between items-center flex-wrap gap-3 md:gap-4">
            <div class="flex flex-col">
                <ProfileEditableField field="full_name" :value="userName" :endpoints="endpoints" class="min-h-7 md:min-h-8" />
                <div class="text-ink-muted flex gap-4 md:gap-6 min-h-7 md:min-h-8">
                    <ProfileTag v-if="yearsOfExperience" :model-value="yearsOfExperience" icon="bi-graph-up-arrow" />
                    <ProfileEditableField v-if="jobTitle" field="job_title" :value="jobTitle" :endpoints="endpoints" />
                </div>
            </div>
            <div class="ml-auto">
                <div class="flex items-center gap-2 justify-between">
                    <p class="body text-ink-muted">Profile Strength</p>
                    <BaseProgressBar :value="completionPercent" :aria-label="`Profile Strength: ${completionPercent}%`" show-label class="w-48" />
                </div>
                <p v-if="!isComplete" class="body-small text-ink-muted">Add more insights to boost Nadia's understanding.</p>
            </div>
        </div>
        <HorizontalDivider class="my-0!" />
        <div class="flex flex-col items-center gap-3 md:gap-4 text-center">
            <h2 class="display-serif">Your Coaching Journey</h2>
            <p class="body-small text-ink-muted max-w-91.5">Every conversation helps Nadia understand your world — unlocking smarter, more personalized coaching.</p>
            <BaseProgressBar class="w-3/4 relative top-9.5 z-0" :value="barPercent" size="xs" aria-label="Coaching journey progress" />
            <div class="grid grid-cols-4 w-full items-start justify-center">
                <!-- progress step circle -->
                <div v-for="step in progressSteps" :key="step.id" class="flex flex-col items-center">
                    <ProgressCircle
                        class="min-w-10 max-w-10 min-h-10 relative mb-4"
                        aria-hidden="true"
                        :class="[currentProgressStep.id === step.id && 'scale-125 shadow-md']"
                        content=""
                        :progress="currentProgressStep.id > step.id ? 100 : currentProgressStep.id === step.id ? (completionPercent % 33) * 3 : 0"
                        gradient-from="var(--color-progress-fill)"
                        gradient-to="var(--color-progress-track)">
                        <i class="bi bi-stars text-base/none absolute inset-x-0 top-3" aria-hidden="true"></i>
                    </ProgressCircle>
                    <p :class="[currentProgressStep.id === step.id ? 'body-small-bold md:body-bold' : 'body-small md:body']">{{ step.label }}</p>
                    <p v-if="currentProgressStep.id === step.id" class="details text-ink-muted hidden lg:inline">{{ step.subtitle }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import ProgressCircle from "~vue/components/ProgressCircle.vue";
import ProfileEditableField from "~vue/UserProfile/ProfileEditableField.vue";
import { computed } from "vue";

import ProfileTag from "./ProfileTag.vue";
import type { UserProfileEndpoints } from "./types";

type ProfileHeaderProps = {
    userName: string;
    jobTitle: string | null;
    yearsOfExperience: string | null;
    completionPercent: number;
    endpoints: UserProfileEndpoints;
};

const { endpoints, completionPercent, jobTitle = "", yearsOfExperience = "" } = defineProps<ProfileHeaderProps>();

const isComplete = computed(() => completionPercent === 100);

const progressSteps = [
    { id: 1, label: "Starting Out", subtitle: "Begin your journey with Nadia." },
    { id: 2, label: "Getting to know you", subtitle: "Nadia is learning about your world." },
    { id: 3, label: "Building a Picture", subtitle: "Understanding your goals and challenges." },
    { id: 4, label: "Coaching with Insight", subtitle: "Tailored coaching based on your unique profile." },
];

const currentProgressStep = computed(() => {
    if (completionPercent === 100) {
        return progressSteps[3];
    } else if (completionPercent >= 66) {
        return progressSteps[2];
    } else if (completionPercent >= 33) {
        return progressSteps[1];
    } else {
        return progressSteps[0];
    }
});

const barPercent = computed(() => {
    return (currentProgressStep.value.id - 1) * 33.33;
});
</script>

<style scoped></style>
